// Copyright (c) 2026 Goffy. All Rights Reserved.

export type RoundTeamLayout = {
  teamId: string;
  playerIds: string[];
};

export type RoundGroupLayout = {
  groupId: string;
  playerIds: string[];
  teams: RoundTeamLayout[];
};

export type RoundGroupsLayout = {
  roundId: string;
  baselinePlayerOrder: string[];
  effectivePlayerOrder: string[];
  groups: RoundGroupLayout[];
};

const MAX_GROUP_SIZE = 4;

export function buildRoundGroupsLayout(
  roundId: string,
  baselinePlayerOrder: ReadonlyArray<string>,
  manualPlayerOrder: ReadonlyArray<string> | null,
  teamIndexByPlayerId?: Readonly<Record<string, number>> | null
): RoundGroupsLayout {
  const baseline = normalizeUniqueOrder(baselinePlayerOrder);
  const manual = normalizeUniqueOrder(manualPlayerOrder ?? []).filter((playerId) => baseline.includes(playerId));
  const manualSet = new Set(manual);
  const effectiveOrder = [...manual, ...baseline.filter((playerId) => !manualSet.has(playerId))];

  const groupSizes = resolveGroupSizes(effectiveOrder.length);
  const groups: RoundGroupLayout[] = [];
  let offset = 0;

  groupSizes.forEach((groupSize, groupIndex) => {
    const groupPlayers = effectiveOrder.slice(offset, offset + groupSize);
    offset += groupSize;

    const teamMembers = resolveTeamMembers(groupPlayers, teamIndexByPlayerId ?? null);
    const teams: RoundTeamLayout[] = teamMembers.map((teamPlayers, teamIndex) => ({
      teamId: `${roundId}:group:${groupIndex + 1}:team:${teamIndex + 1}`,
      playerIds: teamPlayers,
    }));

    groups.push({
      groupId: `${roundId}:group:${groupIndex + 1}`,
      playerIds: groupPlayers,
      teams,
    });
  });

  return {
    roundId,
    baselinePlayerOrder: baseline,
    effectivePlayerOrder: effectiveOrder,
    groups,
  };
}

// A group's manual team arrangement only applies when every member carries an assignment;
// partial coverage (e.g. a newly added player) falls back to the default split. Mirrors
// RoundGroupingService.ResolveTeamMembers on the backend.
function resolveTeamMembers(
  groupPlayers: string[],
  teamIndexByPlayerId: Readonly<Record<string, number>> | null
): string[][] {
  if (
    teamIndexByPlayerId &&
    groupPlayers.length > 0 &&
    groupPlayers.every((playerId) => {
      const teamIndex = teamIndexByPlayerId[playerId];
      return teamIndex === 0 || teamIndex === 1;
    })
  ) {
    return [0, 1]
      .map((teamIndex) => groupPlayers.filter((playerId) => teamIndexByPlayerId[playerId] === teamIndex))
      .filter((members) => members.length > 0);
  }

  const teamSizes = resolveTeamSizes(groupPlayers.length);
  const members: string[][] = [];
  let teamOffset = 0;
  teamSizes.forEach((teamSize) => {
    members.push(groupPlayers.slice(teamOffset, teamOffset + teamSize));
    teamOffset += teamSize;
  });
  return members;
}

/**
 * Group numbers (1-based) whose teams the user must even out before the arrangement can be
 * saved: two teams differing by more than one player (3v1), or three-plus players in a
 * single team. The unavoidable 2v1 of an odd group and the default single team of a one- or
 * two-player group stay valid. Mirrors RoundGroupingService.FindUnevenGroupNumbers.
 */
export function findUnevenGroupNumbers(layout: RoundGroupsLayout): number[] {
  const uneven: number[] = [];
  layout.groups.forEach((group, index) => {
    const sizes = group.teams.map((team) => team.playerIds.length).filter((size) => size > 0);
    const isUneven =
      sizes.length >= 2
        ? Math.max(...sizes) - Math.min(...sizes) > 1
        : sizes.length === 1 && group.playerIds.length > 2;
    if (isUneven) {
      uneven.push(index + 1);
    }
  });
  return uneven;
}

/**
 * Move a player into a target team of their own group, materializing the group's team
 * assignments (every member gets an explicit index) so the arrangement round-trips through
 * the server. Returns null for cross-group targets — those stay swaps.
 */
export function applyTeamMove(
  layout: RoundGroupsLayout,
  teamIndexByPlayerId: Readonly<Record<string, number>>,
  sourcePlayerId: string,
  targetGroupIndex: number,
  targetTeamIndex: number
): Record<string, number> | null {
  const group = layout.groups[targetGroupIndex];
  if (!group || !group.playerIds.includes(sourcePlayerId)) {
    return null;
  }
  if (targetTeamIndex !== 0 && targetTeamIndex !== 1) {
    return null;
  }

  const next: Record<string, number> = { ...teamIndexByPlayerId };
  group.teams.forEach((team, teamIndex) => {
    team.playerIds.forEach((playerId) => {
      next[playerId] = teamIndex;
    });
  });
  next[sourcePlayerId] = targetTeamIndex;
  return next;
}

/**
 * Rebuild the per-player team map from a server groups payload so local edits start from
 * the arrangement the server actually persisted.
 */
export function deriveTeamIndexByPlayerId(
  groups: ReadonlyArray<{ teams: ReadonlyArray<{ playerIds: ReadonlyArray<string> }> }>
): Record<string, number> {
  const map: Record<string, number> = {};
  groups.forEach((group) => {
    group.teams.forEach((team, teamIndex) => {
      team.playerIds.forEach((playerId) => {
        map[playerId] = teamIndex;
      });
    });
  });
  return map;
}

/** Swap two players' manual team assignments along with their positions. */
export function swapTeamIndexes(
  teamIndexByPlayerId: Readonly<Record<string, number>>,
  playerIdA: string,
  playerIdB: string
): Record<string, number> {
  const next: Record<string, number> = { ...teamIndexByPlayerId };
  const teamA = next[playerIdA];
  const teamB = next[playerIdB];
  delete next[playerIdA];
  delete next[playerIdB];
  if (teamB !== undefined) {
    next[playerIdA] = teamB;
  }
  if (teamA !== undefined) {
    next[playerIdB] = teamA;
  }
  return next;
}

export function applyPlayerSwap(
  playerOrder: ReadonlyArray<string>,
  sourcePlayerId: string,
  targetPlayerId: string
): string[] {
  if (!sourcePlayerId || !targetPlayerId || sourcePlayerId === targetPlayerId) {
    return [...playerOrder];
  }

  const next = [...playerOrder];
  const sourceIndex = next.indexOf(sourcePlayerId);
  const targetIndex = next.indexOf(targetPlayerId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return next;
  }

  next[sourceIndex] = targetPlayerId;
  next[targetIndex] = sourcePlayerId;
  return next;
}

function normalizeUniqueOrder(order: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  order.forEach((playerId) => {
    if (!playerId) return;
    if (seen.has(playerId)) return;
    seen.add(playerId);
    normalized.push(playerId);
  });
  return normalized;
}

function resolveGroupSizes(playerCount: number): number[] {
  if (playerCount <= 0) return [];
  if (playerCount === 1) return [1];

  const fullGroups = Math.floor(playerCount / MAX_GROUP_SIZE);
  const remainder = playerCount % MAX_GROUP_SIZE;
  const sizes = new Array(fullGroups).fill(MAX_GROUP_SIZE);

  if (remainder === 0) return sizes;
  if (remainder === 1) {
    if (fullGroups > 0) {
      return [...new Array(fullGroups - 1).fill(MAX_GROUP_SIZE), 3, 2];
    }
    return [1];
  }
  if (remainder === 2) return [...sizes, 2];
  return [...sizes, 3];
}

function resolveTeamSizes(groupSize: number): number[] {
  if (groupSize <= 0) return [];
  if (groupSize === 1) return [1];
  if (groupSize === 2) return [2];
  if (groupSize === 3) return [2, 1];
  if (groupSize === 4) return [2, 2];
  throw new Error(`Unsupported group size: ${groupSize}`);
}
